import axios from 'axios';
import { getOrgName, getTenantFromSubdomain } from '../../utils/appendOrgQuery';
import { eventEmitter } from '../../utils/eventEmitter';

const handleFileUpload = async (file, cancelToken, reference, downloadable = false) => {
  try {
    const organization = getOrgName();
    const tenant = getTenantFromSubdomain();
    const Token = localStorage.getItem('Token');

    // Choose larger chunk sizes for better throughput on large files
    const chooseChunkSize = (sizeBytes) => {
      // 64MB for files >= 2GB, else 32MB
      const MB = 1024 * 1024;
      if (sizeBytes >= 2 * 1024 * 1024 * 1024) return 64 * MB;
      return 32 * MB;
    };

    const chunkSize = chooseChunkSize(file.size);
    const totalChunks = Math.ceil(file.size / chunkSize);

    // Binary upload endpoint (sibling to JSON one). Allow override via env
    const baseUrl = (process.env.REACT_APP_URL || '').replace(/\/+$/, '');
    const endpoint = `${baseUrl}/v2/upload/binary`;

    const postChunk = async (chunkBlob, metaHeaders) => {
      const chunkHeaders = {
        'Content-Type': 'application/octet-stream',
        Authorization: `Bearer ${Token}`,
        ...metaHeaders,
      };

      // Include tenant header if subdomain exists
      if (tenant) {
        chunkHeaders.tenant = tenant;
      }

      // Include organization header if orgName is available
      if (organization) {
        chunkHeaders.organization = organization;
      }

      const response = await axios.post(endpoint, chunkBlob, {
        headers: chunkHeaders,
        // Important: keep token for cancellation; browser handles keep-alive/HTTP2
        cancelToken: cancelToken,
        timeout: 120000, // 120s per-chunk timeout to avoid indefinite hangs
      });
      return response.data;
    };

    const retry = async (fn, { attempts = 3, baseDelayMs = 500 }) => {
      let lastErr;
      for (let attempt = 1; attempt <= attempts; attempt++) {
        if (cancelToken?.reason) throw cancelToken.reason;
        try {
          return await fn();
        } catch (err) {
          lastErr = err;
          if (axios.isCancel(err)) throw err;
          const jitter = Math.random() * 100;
          const delay = baseDelayMs * Math.pow(2, attempt - 1) + jitter;
          await new Promise((r) => setTimeout(r, delay));
        }
      }
      throw lastErr;
    };

    const emitProgress = (progress) => {
      try {
        eventEmitter.emit('uploadProgress', progress);
      } catch (_) {}
    };

    // Handle single-chunk small files via binary endpoint
    if (totalChunks === 1) {
      const chunk = file.slice(0, file.size);
      const headers = {
        'X-File-Name': encodeURIComponent(file.name),
        'X-File-Size': String(file.size),
        'X-Chunk-Index': '0',
        'X-Total-Chunks': String(totalChunks),
        'X-Reference': reference || '',
        'X-Downloadable': String(!!downloadable),
      };
      const result = await retry(() => postChunk(chunk, headers), { attempts: 3 });
      if (result?.status === 'completed') {
        emitProgress(100);
        return result.publicUrl || null;
      }
      return null;
    }

    // 1) Upload first chunk sequentially to obtain uploadId
    if (cancelToken?.reason) return null;
    let uploadId = null;
    const firstStart = 0;
    const firstEnd = Math.min(chunkSize, file.size);
    const firstChunk = file.slice(firstStart, firstEnd);

    const firstHeaders = {
      'X-File-Name': encodeURIComponent(file.name),
      'X-File-Size': String(file.size),
      'X-Chunk-Index': '0',
      'X-Total-Chunks': String(totalChunks),
      'X-Reference': reference || '',
      'X-Downloadable': String(!!downloadable),
      // organization header is now sent on all chunks via postChunk
    };

    const firstResult = await retry(() => postChunk(firstChunk, firstHeaders), { attempts: 5 });
    if (!firstResult?.success) {
      throw new Error(`Failed to upload first chunk: ${firstResult?.message || 'Unknown error'}`);
    }
    if (firstResult.uploadId) {
      uploadId = firstResult.uploadId;
    }
    if (firstResult.status === 'completed') {
      emitProgress(100);
      return firstResult.publicUrl || null;
    }

    // 2) Upload remaining chunks in parallel with controlled concurrency (8–12)
    const desiredMax = 12;
    const minMax = 8;
    const hwc =
      typeof navigator !== 'undefined' && navigator.hardwareConcurrency
        ? navigator.hardwareConcurrency
        : null;
    const computed = hwc ? Math.floor(hwc * 0.75) : 8;
    const maxParallelUploads = Math.max(minMax, Math.min(desiredMax, computed));

    let completedChunks = 1; // first chunk done
    let completedUrl = null;

    const uploadSingleChunk = async (chunkIndex) => {
      if (cancelToken?.reason) return;
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const headers = {
        'X-Upload-Id': uploadId || '',
        'X-File-Name': encodeURIComponent(file.name),
        'X-File-Size': String(file.size),
        'X-Chunk-Index': String(chunkIndex),
        'X-Total-Chunks': String(totalChunks),
        'X-Reference': reference || '',
        'X-Downloadable': String(!!downloadable),
        // organization header is sent via postChunk
      };

      const result = await retry(() => postChunk(chunk, headers), { attempts: 5 });
      if (!result?.success) throw new Error(`Failed to upload chunk ${chunkIndex}`);

      completedChunks += 1;
      if (result.status === 'completed' && result.publicUrl && !completedUrl) {
        completedUrl = result.publicUrl;
      }
      const progress = Math.floor((completedChunks / totalChunks) * 100);
      emitProgress(progress);
    };

    const indices = Array.from({ length: totalChunks - 1 }, (_, i) => i + 1);
    const runWithConcurrency = async (items, worker, concurrency) => {
      let current = 0;
      const workers = new Array(Math.min(concurrency, items.length)).fill(null).map(async () => {
        while (current < items.length) {
          const idx = current++;
          const item = items[idx];
          await worker(item);
        }
      });
      await Promise.all(workers);
    };

    await runWithConcurrency(indices, uploadSingleChunk, maxParallelUploads);

    // If final URL already known, return immediately
    if (completedUrl) {
      return completedUrl;
    }

    // Poll for composition completion if last chunk response didn’t include the URL
    if (uploadId) {
      try {
        const pollBase = `${baseUrl}/v2/upload/progress/${uploadId}`;
        const maxWaitMs = 5 * 60 * 1000; // 5 minutes
        let stepMs = 500;
        let waited = 0;
        while (waited < maxWaitMs) {
          if (cancelToken?.reason) return null;
          const pollHeaders = {
            Authorization: `Bearer ${Token}`,
          };

          // Include tenant header if subdomain exists
          if (tenant) {
            pollHeaders.tenant = tenant;
          }

          // Include organization header if orgName is available
          if (organization) {
            pollHeaders.organization = organization;
          }

          const res = await axios.get(pollBase, {
            headers: pollHeaders,
          });
          if (res?.data?.success && res.data.status === 'completed' && res.data.publicUrl) {
            emitProgress(100);
            return res.data.publicUrl;
          }
          emitProgress(100); // keep loader at 100% while finalizing
          await new Promise((r) => setTimeout(r, stepMs));
          waited += stepMs;
          // mild backoff up to 2s
          if (stepMs < 2000) stepMs += 250;
        }
      } catch (_) {
        // ignore and fallthrough
      }
    }

    return null;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.error('Upload cancelled');
      return null;
    } else {
      console.error('Upload error:', error);
      if (error?.response?.data?.errorType === 'RestrictionError') {
        eventEmitter.emit('showStorageLimitRestrictionPopup');
      }
    }
    return null;
  }
};

export default handleFileUpload;
